/* eslint-disable */


import React, { useEffect, useMemo, useState } from "react";
import { AccountLink } from "../components/resellers/AccountLink";
import { useParams, useNavigate, Link } from "react-router-dom";
import { pdf } from "@react-pdf/renderer";
import { api } from "../lib/api";
import { sortByName } from "../lib/utils";
import { fmtDate, fmtDateTime, fmtNum, isoToLocalInput, localInputToIso } from "../lib/format";
import { ArrowLeft, ClipboardList, Factory, FileText, FileSignature, CheckCircle2, XCircle, ExternalLink, Package, AlertTriangle, Pencil, Save, X, Plus, Trash2, EyeOff, Eye, CalendarClock, Link2, Copy, Printer, PackageCheck, TrendingDown, Loader2, MoreHorizontal, Truck as TruckIcon } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel } from "../components/ui/dropdown-menu";
import CancelCascadeDialog from "../components/CancelCascadeDialog";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useConfirm } from "../components/ConfirmDialog";
import PackInsertDialog from "../components/packs/PackInsertDialog";
import { showPdfPreview } from "../components/PdfPreview";
import { ClientOrderPdfDocument } from "../components/ClientOrderPdf";
import { generateShippingLabelBlob } from "../components/ShippingLabelPdf";
import { toast } from "sonner";

const STATUS_META = {
  NOUVELLE: { label: "Projet", className: "bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D3A8]" },
  VALIDEE: { label: "Validée", className: "bg-[#E5EFF8] text-[#2D5A87] border-[#C5D8E8]" },
  EN_PRODUCTION: { label: "En production", className: "bg-[#E5EFF8] text-[#2D5A87] border-[#C5D8E8]" },
  PRETE: { label: "Prête", className: "bg-[#EFEAFA] text-[#5847A6] border-[#D4C8F0]" },
  LIVREE: { label: "Livrée", className: "bg-[#EAF4EE] text-[#1F4E3D] border-[#C5DBCF]" },
  FACTUREE: { label: "Facturée", className: "bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D3A8]" },
  PAYEE: { label: "Payée", className: "bg-[#EAF4EE] text-[#1F4E3D] border-[#C5DBCF]" },
  ANNULEE: { label: "Annulée", className: "bg-[#FCEEEA] text-[#C05C47] border-[#E8C1B4]" },
};

// Phase 101bx (18/02/2026) — Utilise les helpers Zurich (isoToLocalInput /
// localInputToIso) exportés de lib/format.js pour éviter le décalage TZ
// (browser vs stockage UTC) lors des saisies de date de livraison.
const toLocalInput = isoToLocalInput;

export default function ClientOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [co, setCo] = useState(null);
  const [bl, setBl] = useState(null); // Phase 87ak — état complet du BL lié (pour transfer_executed)
  const [availability, setAvailability] = useState(null);
  const [products, setProducts] = useState([]);
  const [distributors, setDistributors] = useState([]);

  // Mode édition
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  // Édition rapide de la date de livraison (reste possible même si commande verrouillée)
  const [editingDate, setEditingDate] = useState(false);
  const [dateDraft, setDateDraft] = useState("");
  // Phase 101du.7 (Feb 2026) — édition de order_date après création (cas : saisie
  // dans le système J+2 alors que le client a passé la commande J-1).
  const [editingOrderDate, setEditingOrderDate] = useState(false);
  const [orderDateDraft, setOrderDateDraft] = useState("");
  const [savingOrderDate, setSavingOrderDate] = useState(false);
  const [savingDate, setSavingDate] = useState(false);

  // Phase 86b — Duplication d'une commande (cas typique : client récurrent
  // qui recommande la même chose). Crée une nouvelle commande en statut
  // NOUVELLE avec client + lignes + prix copiés, puis redirige.
  const [duplicating, setDuplicating] = useState(false);
  const duplicate = async () => {
    if (duplicating) return;
    const ok = await confirm({
      title: "Dupliquer cette commande ?",
      description: `Une nouvelle commande sera créée en statut "Projet", avec le même client et les mêmes lignes (prix négociés inclus). Aucun lien avec ${co.reference} (OF, BL, facture) ne sera repris.`,
      confirmText: "Dupliquer",
      cancelText: "Annuler",
    });
    if (!ok) return;
    setDuplicating(true);
    try {
      const r = await api.post(`/client-orders/${co.id}/duplicate`);
      toast.success(`Nouvelle commande ${r.data.reference} créée`);
      navigate(`/commandes-clients/${r.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la duplication");
    } finally {
      setDuplicating(false);
    }
  };

  // Phase 101bx (18/02/2026) — Bouton "Débloquer" pour nettoyer les références
  // orphelines (verrou _GENERATING_ ou bl_id/of_ids/invoice_id supprimés).
  // Cas d'usage : generate-bl échoue en plein milieu, laisse bl_id à "_GENERATING_"
  // → la CMD n'accepte plus ni regen ni delete. Ce bouton dénoue la commande.
  const [unlocking, setUnlocking] = useState(false);
  const unlockCo = async () => {
    if (unlocking) return;
    const ok = await confirm({
      title: "Débloquer cette commande ?",
      description: "Nettoie les références orphelines (verrou de génération BL, BL/OF/facture supprimés référencés à tort). Utilise cette action si tu vois « Un BL existe déjà » ou « Impossible de supprimer, déjà liée » alors qu'il n'y a rien de visible.",
      confirmText: "Débloquer",
      cancelText: "Annuler",
    });
    if (!ok) return;
    setUnlocking(true);
    try {
      const { data } = await api.post(`/client-orders/${co.id}/unlock`);
      toast.success(data.message || "Commande débloquée");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setUnlocking(false);
    }
  };

  // Phase 87y — Réparation des liens OF orphelins.
  // Utile quand la fiche commande affiche "X OF lié" mais que la liste filtrée
  // des OFs est vide (cas Cameroni en prod, post-PAYEE où _resync_client_order_state
  // ne s'exécutait pas). Nettoie of_ids, recolle les OFs orphelins, et pour les
  // commandes web sans OF actif, déclenche une nouvelle auto-OF dédiée.
  const [repairing, setRepairing] = useState(false);
  const repairLinks = async () => {
    if (repairing) return;
    setRepairing(true);
    try {
      const { data } = await api.post(`/client-orders/${co.id}/repair-links`);
      const removed = (data.removed_of_ids || []).length;
      const reattached = (data.reattached_orphan_ofs || []).length;
      const created = data.auto_of_created;
      const bits = [];
      if (removed) bits.push(`${removed} OF orphelin(s) retiré(s)`);
      if (reattached) bits.push(`${reattached} OF re-rattaché(s)`);
      if (created) bits.push(`OF auto-créé ${created.reference}`);
      if (data.bl_repair) bits.push("BL orphelin retiré");
      if (data.status_fix) bits.push(`Statut corrigé : ${data.status_fix.from} → ${data.status_fix.to}`);
      toast.success(bits.length ? `Réparation OK : ${bits.join(" · ")}` : "Aucune anomalie détectée", { duration: 6000 });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la réparation");
    } finally {
      setRepairing(false);
    }
  };

  // Phase 86d — Génération PDF "Bon de commande" pour impression/envoi client
  const [printing, setPrinting] = useState(false);
  const printPdf = async () => {
    if (printing) return;
    setPrinting(true);
    try {
      // On va chercher les settings à la volée pour avoir le bon en-tête société
      let settings = null;
      try {
        const r = await api.get("/settings");
        settings = r.data;
      } catch (e) {
        // pas bloquant : on fallback sur des défauts dans le PDF
        settings = null;
      }
      const blob = await pdf(<ClientOrderPdfDocument co={co} settings={settings} />).toBlob();
      const filename = `BC-${co.reference || co.id}.pdf`;
      showPdfPreview(blob, filename);
    } catch (e) {
      toast.error("Erreur lors de la génération du PDF");
      console.error(e);
    } finally {
      setPrinting(false);
    }
  };

  // Phase 87v — Étiquette d'expédition A6 (à coller sur le colis La Poste)
  const [printingLabel, setPrintingLabel] = useState(false);
  const printShippingLabel = async () => {
    if (printingLabel) return;
    setPrintingLabel(true);
    try {
      let settings = null;
      try {
        const r = await api.get("/settings");
        settings = r.data;
      } catch (e) {
        settings = null;
      }
      const blob = await generateShippingLabelBlob({ co, settings }, { pdf });
      const filename = `ETIQ-${co.reference || co.id}.pdf`;
      showPdfPreview(blob, filename);
    } catch (e) {
      toast.error("Erreur lors de la génération de l'étiquette");
      console.error(e);
    } finally {
      setPrintingLabel(false);
    }
  };

  const load = async () => {
    const [c, p, d] = await Promise.all([
      api.get(`/client-orders/${id}`),
      api.get(`/products`),
      api.get(`/distributors`),
    ]);
    setCo(c.data);
    setProducts(p.data);
    setDistributors(d.data);
    // Phase 87ak — charge le BL lié pour connaître son statut de transfert
    if (c.data?.bl_id) {
      try {
        const b = await api.get(`/delivery-notes/${c.data.bl_id}`);
        setBl(b.data);
      } catch (_) { setBl(null); }
    } else {
      setBl(null);
    }
    try {
      const a = await api.get(`/client-orders/${id}/stock-availability`);
      setAvailability(a.data);
    } catch (_) {
      setAvailability(null);
    }
  };
  useEffect(() => { load(); }, [id]);

  const productMap = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);
  const clients = useMemo(() => distributors.filter((d) => d.type === "CLIENT"), [distributors]);

  const isEditable = co
    && co.status === "NOUVELLE"
    && !co.bl_id
    && !co.invoice_id
    && (co.of_ids || []).length === 0;

  const startEdit = () => {
    setDraft({
      distributor_id: co.distributor_id,
      delivery_date_target: toLocalInput(co.delivery_date_target),
      notes: co.notes || "",
      lines: (co.lines || []).map((ln) => ({
        product_id: ln.product_id,
        product_name: ln.product_name,
        product_sku: ln.product_sku,
        quantity: ln.quantity,
        unit: ln.unit,
        unit_price: ln.unit_price ?? "",
        vat_rate: ln.vat_rate ?? 2.6,
        notes: ln.notes || "",
        unavailable: !!ln.unavailable,
        pack_id: ln.pack_id || null,
        pack_label: ln.pack_label || null,
      })),
    });
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setDraft(null);
    setIsEditing(false);
  };

  const updateDraftLine = (idx, patch) =>
    setDraft((d) => ({ ...d, lines: d.lines.map((ln, i) => (i === idx ? { ...ln, ...patch } : ln)) }));
  const addDraftLine = () =>
    setDraft((d) => ({
      ...d,
      lines: [...d.lines, { product_id: "", quantity: 1, unit_price: "", vat_rate: 2.6, notes: "", unavailable: false, preselected_lot_id: "" }],
    }));
  const removeDraftLine = (idx) =>
    setDraft((d) => ({ ...d, lines: d.lines.filter((_, i) => i !== idx) }));

  // Phase 101bu — cache lots dispo par produit (Conthey), triés DLC ASC (plus courtes en premier).
  const [lotsByProduct, setLotsByProduct] = useState({});
  const fetchLotsForProduct = async (pid) => {
    if (!pid || lotsByProduct[pid]) return;
    try {
      // Phase 101bw — Pour la revente : inclure les lots dédiés (stock physique fongible).
      const includeDedicated = co?.sale_type === "resale" ? "&include_dedicated=true" : "";
      const r = await api.get(`/moma-refills/available-lots/${pid}?for_client_order_id=${id}${includeDedicated}`);
      // Tri DLC DESC — DLC la plus LONGUE en premier (l'utilisatrice les préfère)
      const sortedLots = (r.data?.lots || []).slice().sort((a, b) => {
        const da = a.dlc || "0000";
        const db = b.dlc || "0000";
        return db.localeCompare(da); // DESC
      });
      setLotsByProduct((prev) => ({ ...prev, [pid]: sortedLots }));
    } catch {
      setLotsByProduct((prev) => ({ ...prev, [pid]: [] }));
    }
  };
  // Précharge les lots pour les produits déjà présents dans la commande quand on entre en mode édition.
  useEffect(() => {
    if (!draft || !isEditing) return;
    const uniquePids = [...new Set(draft.lines.map((ln) => ln.product_id).filter(Boolean))];
    uniquePids.forEach(fetchLotsForProduct);
  }, [isEditing, draft?.id]);

  const insertPackLines = (packLines) =>
    setDraft((d) => ({
      ...d,
      lines: [
        ...d.lines,
        ...packLines.map((ln) => ({
          product_id: ln.product_id,
          product_name: ln.product_name,
          product_sku: ln.product_sku,
          unit: ln.unit,
          quantity: ln.quantity,
          unit_price: ln.unit_price,
          vat_rate: ln.vat_rate,
          notes: ln.notes || "",
          unavailable: false,
          pack_id: ln.pack_id,
          pack_label: ln.pack_label,
        })),
      ],
    }));

  const onProductChange = (idx, productId) => {
    const p = productMap[productId];
    updateDraftLine(idx, {
      product_id: productId,
      product_name: p?.name || "",
      product_sku: p?.sku || "",
      unit: p?.unit || "pcs",
      preselected_lot_id: "", // reset lot pré-sélectionné à chaque changement de produit
    });
    fetchLotsForProduct(productId);
  };

  const saveEdit = async () => {
    if (!draft.distributor_id) { toast.error("Sélectionnez un client"); return; }
    if (draft.lines.length === 0) { toast.error("Ajoutez au moins une ligne"); return; }
    for (const ln of draft.lines) {
      if (!ln.product_id) { toast.error("Toutes les lignes doivent avoir un produit"); return; }
      if (!ln.quantity || Number(ln.quantity) <= 0) { toast.error("Quantité invalide"); return; }
    }
    setSaving(true);
    try {
      await api.put(`/client-orders/${id}`, {
        distributor_id: draft.distributor_id,
        delivery_date_target: draft.delivery_date_target || null,
        notes: draft.notes,
        lines: draft.lines.map((ln) => ({
          product_id: ln.product_id,
          quantity: Number(ln.quantity),
          unit_price: ln.unit_price === "" || ln.unit_price === null ? null : Number(ln.unit_price),
          vat_rate: ln.vat_rate !== undefined && ln.vat_rate !== "" ? Number(ln.vat_rate) : 2.6,
          notes: ln.notes || "",
          unavailable: !!ln.unavailable,
          pack_id: ln.pack_id || null,
          pack_label: ln.pack_label || null,
          preselected_lot_id: ln.preselected_lot_id || null,
        })),
      });
      toast.success("Modifications enregistrées");
      setIsEditing(false);
      setDraft(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (status) => {
    try {
      await api.put(`/client-orders/${id}`, { status });
      toast.success(`Statut : ${STATUS_META[status]?.label}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const startEditDate = () => {
    setDateDraft(toLocalInput(co.delivery_date_target));
    setEditingDate(true);
  };
  const saveDate = async () => {
    if (!dateDraft) {
      toast.error("Date invalide");
      return;
    }
    setSavingDate(true);
    try {
      // Phase 101bx — Convertit datetime-local Zurich en ISO UTC via helper.
      const iso = localInputToIso(dateDraft);
      if (!iso) { toast.error("Date invalide"); setSavingDate(false); return; }
      await api.put(`/client-orders/${id}`, { delivery_date_target: iso });
      toast.success("Date de livraison mise à jour");
      if (co.bl_id) toast.info("Le BL lié a été synchronisé automatiquement");
      setEditingDate(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSavingDate(false);
    }
  };

  // Phase 101du.7 — édition de order_date (date à laquelle le client a passé
  // la commande, pas la date de saisie système). Autorisé tant que la commande
  // n'est pas ANNULEE (même si LIVREE/FACTUREE : l'admin peut vouloir corriger
  // rétroactivement pour la compta).
  const startEditOrderDate = () => {
    setOrderDateDraft(toLocalInput(co.order_date || co.created_at));
    setEditingOrderDate(true);
  };
  const saveOrderDate = async () => {
    if (!orderDateDraft) {
      toast.error("Date invalide");
      return;
    }
    setSavingOrderDate(true);
    try {
      const iso = localInputToIso(orderDateDraft);
      if (!iso) { toast.error("Date invalide"); setSavingOrderDate(false); return; }
      await api.put(`/client-orders/${id}`, { order_date: iso });
      toast.success("Date de commande mise à jour");
      setEditingOrderDate(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSavingOrderDate(false);
    }
  };

  const generateBl = async (forceUseStock = false) => {
    const isResellerOrder = !!co?.reseller_id;

    // Phase 101bv — Rafraîchir l'availability JUSTE avant le check pour éviter
    // un state UI stale (l'utilisateur a pu modifier la commande depuis).
    let freshAvail = availability;
    try {
      const r = await api.get(`/client-orders/${id}/stock-availability${forceUseStock ? "?force_use_stock=true" : ""}`);
      freshAvail = r.data;
      setAvailability(freshAvail);
    } catch (_) { /* on garde l'état actuel en cas d'échec réseau */ }

    const useDedicatedOnly = freshAvail?.use_dedicated_only && !forceUseStock;

    // Phase 66 — Commande revendeur en mode fabrication dédiée
    if (isResellerOrder && useDedicatedOnly && !freshAvail?.is_fully_servable) {
      const ok = await confirm({
        title: "Lots dédiés insuffisants",
        description:
          "Cette commande revendeur est en mode FABRICATION DÉDIÉE : seuls les lots produits expressément pour cette commande sont utilisés (DLC maximale en boutique). " +
          "Vous pouvez soit produire un OF dédié, soit forcer l'utilisation du stock distributeur existant (FIFO classique).",
        confirmText: "Forcer le stock existant (FIFO)",
        cancelText: "Annuler",
        variant: "destructive",
      });
      if (!ok) return;
      // Relance en mode override
      return generateBl(true);
    }

    if (!freshAvail?.is_fully_servable && !forceUseStock) {
      // Phase 101bv — Message détaillé : quel produit / combien il manque / cause.
      const missingLines = (freshAvail?.lines || []).filter((ln) => !ln.unavailable && !ln.is_complete);
      const pending = missingLines.filter((ln) => ln.pending_lot_selection);
      if (pending.length > 0) {
        const names = pending.map((ln) => ln.product_name).join(", ");
        toast.error(`Choix du lot manquant : ${names}. Éditez la commande et sélectionnez un lot pour chaque produit.`);
        return;
      }
      const details = missingLines.map((ln) => `${ln.product_name} (manque ${ln.quantity_missing})`).join(" · ");
      toast.error(details
        ? `Stock insuffisant : ${details}. Vérifiez les lots choisis dans la commande.`
        : "Stock insuffisant — créez un OF de production ou attendez le réapprovisionnement");
      return;
    }

    const description = isResellerOrder && !forceUseStock
      ? "Lots dédiés à cette commande revendeur (fabrication sur mesure, DLC maximale)."
      : forceUseStock
        ? "⚠️ Mode forcé : prélèvement dans le stock distributeur existant (FIFO par DLC croissante)."
        : "Les lots seront prélevés automatiquement depuis les distributeurs (priorité aux automates, FIFO par DLC).";

    const ok = await confirm({
      title: forceUseStock ? "Forcer le stock existant ?" : "Générer le bon de livraison ?",
      description,
      confirmText: "Générer le BL",
    });
    if (!ok) return;
    try {
      const params = forceUseStock ? "?force_use_stock=true" : "";
      await api.post(`/client-orders/${id}/generate-bl${params}`);
      toast.success(forceUseStock ? "BL généré (mode forcé)" : "BL généré");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  // Phase 87ak — Sortie de stock 1-clic depuis la fiche commande client
  const [shippingInProgress, setShippingInProgress] = useState(false);
  const executeBlTransfer = async () => {
    if (!co?.bl_id || !bl) return;
    // Phase 101dm — Adapte le message selon la nature du destinataire
    const destDist = (distributors || []).find((d) => d.id === bl.distributor_id) || null;
    const isOut = bl.distributor_id === "shop-online-virtual"
      || (destDist && destDist.type !== "AUTOMATIQUE");
    const description = isOut
      ? `Les lots du BL ${bl.reference} seront déduits du dépôt source « ${bl.from_distributor_name || ""} » et livrés directement à ${bl.distributor_name || "le client"} (aucun stock ne reste chez le destinataire, traçabilité HACCP conservée via le BL). Irréversible — corriger via la page BL si besoin.`
      : `Les lots du BL ${bl.reference} seront physiquement déduits du dépôt source « ${bl.from_distributor_name || ""} » et transférés vers le distributeur automatique ${bl.distributor_name || ""}. Irréversible — corriger via la page BL si besoin.`;
    const ok = await confirm({
      title: "Exécuter la sortie de stock ?",
      description,
      confirmText: "Exécuter la sortie",
    });
    if (!ok) return;
    setShippingInProgress(true);
    try {
      await api.post(`/delivery-notes/${co.bl_id}/execute-transfer`);
      toast.success(`Sortie de stock exécutée — lots déduits du dépôt ${bl.from_distributor_name || "source"}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la sortie de stock");
    } finally {
      setShippingInProgress(false);
    }
  };

  const generateOf = async () => {
    // Récupère la date de fabrication réelle via prompt simple (par défaut aujourd'hui)
    const today = new Date().toISOString().slice(0, 10);
    const dateInput = window.prompt(
      "Date de fabrication réelle ? (YYYY-MM-DD)\n" +
      "Cette date sert au calcul de la DLC = date de fab + durée de conservation du produit.\n" +
      "Aujourd'hui par défaut. Si production rétroactive, saisissez la vraie date (ex: 2026-05-30).",
      today
    );
    if (dateInput === null) return; // user cancelled
    const actualDate = (dateInput || "").trim() || null;

    const missing = availability?.lines
      ? availability.lines.filter((ln) => !ln.unavailable && !ln.is_complete)
      : [];
    const description = missing.length > 0
      ? `Un Ordre de Fabrication PLANIFIE sera créé pour les ${(co.lines || []).filter((ln) => !ln.unavailable).length} ligne(s) actives de cette commande. N° de lot auto, DLC calculée à la clôture.`
      : `Stock actuellement suffisant — un OF sera tout de même créé pour produire d'avance. Souhaitez-vous continuer ?`;
    const ok = await confirm({
      title: "Mettre en fabrication ?",
      description: `${description}${actualDate && actualDate !== today ? `\n\n⚠️ Date fabrication réelle : ${actualDate} (saisie rétroactive)` : ""}`,
      confirmText: "Créer l'OF",
    });
    if (!ok) return;
    try {
      const url = actualDate
        ? `/client-orders/${id}/generate-of?actual_production_date=${actualDate}`
        : `/client-orders/${id}/generate-of`;
      await api.post(url);
      toast.success("Ordre de fabrication créé");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const relinkReseller = async () => {
    const ok = await confirm({
      title: "Vérifier / rétablir la liaison revendeur ?",
      description:
        "Cette action recalcule reseller_id en fonction du distributeur lié à un revendeur. "
        + "Si applicable, cascade aux OF (mode fabrication dédiée) et aux lots existants. "
        + "À utiliser si la commande a été créée AVANT d'avoir lié le revendeur à son distributeur ERP.",
      confirmText: "Rétablir",
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/client-orders/${id}/relink-reseller`);
      if (data.ok) {
        toast.success(data.message || "Liaison rétablie");
      } else {
        toast.error(data.message || "Aucun revendeur lié au distributeur de cette commande.");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const applyResellerPrices = async () => {
    const ok = await confirm({
      title: "Appliquer les prix négociés du revendeur ?",
      description:
        "Recalcule le prix unitaire de chaque ligne en appliquant : "
        + "1) les prix négociés individuels du revendeur, "
        + "2) sinon son rabais global, "
        + "3) sinon le tier du distributeur. "
        + "Les prix manuels exceptionnels seront préservés (utilisez « force » via l'API si besoin de tout écraser).",
      confirmText: "Appliquer les prix",
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/client-orders/${id}/apply-reseller-prices`);
      const msg = `${data.lines_updated} ligne(s) mise(s) à jour · ${data.lines_unchanged} déjà correcte(s)`
        + (data.lines_kept_manual > 0 ? ` · ${data.lines_kept_manual} prix manuel(s) préservé(s)` : "");
      toast.success(msg, { duration: 6000 });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  // Phase 101ey (Feb 2026) — Confirmer manuellement un paiement TWINT
  // (le client a payé via lien statique, TWINT ne rappelle pas notre backend :
  // l'admin doit croiser dans son compte TWINT puis cliquer ce bouton).
  const [confirmingTwint, setConfirmingTwint] = useState(false);
  const confirmTwintPayment = async () => {
    const ok = await confirm({
      title: "Confirmer la réception du paiement TWINT ?",
      description:
        `As-tu bien vu le virement de ${(co.lines || []).reduce((s, l) => s + (l.unit_price * l.quantity), 0).toFixed(2)} CHF `
        + `avec la référence « ${co.reference} » dans ton app TWINT ? `
        + `Cette action marque la commande comme PAYÉE et débloque la production/livraison.`,
      confirmText: "Oui, marquer payée",
    });
    if (!ok) return;
    setConfirmingTwint(true);
    try {
      await api.post(`/admin/orders/${id}/confirm-twint`);
      toast.success("Commande marquée TWINT reçue — statut PAYÉE");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la confirmation TWINT");
    } finally {
      setConfirmingTwint(false);
    }
  };

  const generateInvoice = async () => {
    const ok = await confirm({
      title: "Générer la facture (avec QR-Bill) ?",
      description: "Une facture brouillon sera créée à partir des lignes de cette commande. Vous pourrez la modifier puis l'envoyer.",
      confirmText: "Générer la facture",
    });
    if (!ok) return;
    try {
      const lines = (co.lines || [])
        .filter((ln) => !ln.unavailable)
        .map((ln) => ({
          description: `${ln.product_name}${ln.product_sku ? ` (${ln.product_sku})` : ""}`,
          quantity: ln.quantity,
          unit: ln.unit || "pcs",
          unit_price: ln.unit_price || 0,
          vat_rate: ln.vat_rate || 2.6,
        }));
      const { data } = await api.post("/invoices", {
        distributor_id: co.distributor_id,
        client_order_id: co.id,
        additional_info: `Commande ${co.reference}`,
        lines,
      });
      toast.success(`Facture ${data.reference} créée`);
      navigate(`/factures/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  if (!co) return <div className="text-[#575A57]">Chargement…</div>;
  const meta = STATUS_META[co.status] || STATUS_META.NOUVELLE;

  // Totaux : on exclut systématiquement les lignes marquées indisponibles
  const linesForTotal = (isEditing ? draft.lines : co.lines || []).filter((ln) => !ln.unavailable);
  const totalHT = linesForTotal.reduce(
    (acc, ln) => acc + (Number(ln.quantity) || 0) * (Number(ln.unit_price) || 0),
    0
  );
  const totalCost = linesForTotal.reduce((acc, ln) => {
    const prod = products.find((p) => p.id === ln.product_id);
    const c = prod?.cost_price ? Number(prod.cost_price) : 0;
    return acc + (Number(ln.quantity) || 0) * c;
  }, 0);
  const globalMargin = totalHT > 0 && totalCost > 0 && totalHT > totalCost
    ? Math.round(((totalHT - totalCost) / totalHT) * 100)
    : null;

  // Phase 101du.5+ — Économie négociée pour les commandes revendeur.
  // Compare le prix catalogue (`product.price_distributor`) au prix appliqué
  // (`line.unit_price`) et montre l'économie totale + % moyen sur la CMD.
  // Silencieux si : pas de revendeur, ou aucune ligne avec `price_distributor`.
  const savings = (() => {
    if (!co.reseller_id) return null;
    let catalog = 0;
    let negotiated = 0;
    let comparableLines = 0;
    const details = [];
    for (const ln of linesForTotal) {
      const prod = productMap[ln.product_id];
      const catalogUnit = prod?.price_distributor != null ? Number(prod.price_distributor) : null;
      const negotiatedUnit = Number(ln.unit_price) || 0;
      const qty = Number(ln.quantity) || 0;
      if (catalogUnit != null && catalogUnit > 0 && qty > 0) {
        catalog += catalogUnit * qty;
        negotiated += negotiatedUnit * qty;
        comparableLines += 1;
        details.push({
          product_name: ln.product_name,
          quantity: qty,
          catalog_unit: catalogUnit,
          negotiated_unit: negotiatedUnit,
          line_saving: (catalogUnit - negotiatedUnit) * qty,
        });
      }
    }
    if (comparableLines === 0 || catalog <= 0) return null;
    const totalSaving = catalog - negotiated;
    const pctSaving = catalog > 0 ? (totalSaving / catalog) * 100 : 0;
    return {
      catalog: Math.round(catalog * 100) / 100,
      negotiated: Math.round(negotiated * 100) / 100,
      total_saving: Math.round(totalSaving * 100) / 100,
      pct_saving: Math.round(pctSaving * 10) / 10,
      details,
      lines_count: comparableLines,
    };
  })();

  // Workflow steps : Commande → Disponibilité stock → BL → Sortie effectuée → Facture → Payée
  const steps = [
    { key: "new", label: "Commande", icon: ClipboardList, done: true },
    { key: "stock", label: "Stock prêt", icon: Package, done: !!availability?.is_fully_servable || !!co.bl_id },
    { key: "bl", label: "Bon de livraison", icon: FileText, done: !!co.bl_id },
    { key: "shipped", label: "Sortie effectuée", icon: TruckIcon, done: !!bl?.transfer_executed },
    { key: "invoice", label: "Facture", icon: FileSignature, done: !!co.invoice_id },
    { key: "paid", label: "Payée", icon: CheckCircle2, done: co.status === "PAYEE" },
  ];

  return (
    <div className="space-y-6" data-testid="co-detail-page">
      <div className="flex items-center gap-4 flex-wrap">
        <Button variant="outline" size="sm" onClick={() => navigate("/commandes-clients")} data-testid="back-to-co">
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-heading text-[#1A1C1A]">{co.reference}</h1>
            <Badge className={meta.className}>{meta.label}</Badge>
            {isEditable && !isEditing && (
              <span className="text-[11px] uppercase tracking-wider text-[#8C6B2F] bg-[#FBF4E9] border border-[#E8D3A8] px-2 py-0.5 rounded-full inline-flex items-center gap-1" data-testid="editable-badge">
                <Pencil className="w-3 h-3" /> Modifiable
              </span>
            )}
            {!isEditable && co.status !== "ANNULEE" && (
              <span className="text-[11px] uppercase tracking-wider text-[#575A57] bg-[#F0F0EB] border border-[#E5E5E0] px-2 py-0.5 rounded-full" data-testid="locked-badge">
                Verrouillée
              </span>
            )}
          </div>
          <div className="text-sm text-[#575A57] flex items-center gap-2 flex-wrap">
            {/* Phase 87o — Pour les commandes B2C web : préfixe "Client web" (au lieu
                de Distributeur), et on enlève le suffixe " (web)" du nom (redondant). */}
            {co.distributor_id === "shop-online-virtual" ? (
              <span>
                <span className="text-[10px] uppercase tracking-wider mr-1.5" style={{ color: "#8C6B2F" }}>Client web</span>
                <AccountLink resellerId={co.reseller_id} testid="co-detail-account"><strong>{(co.distributor_name || "").replace(/\s*\(web\)\s*$/i, "")}</strong></AccountLink> ·
              </span>
            ) : (
              <span><AccountLink resellerId={co.reseller_id} distributorId={co.distributor_id} testid="co-detail-account">{co.distributor_name}</AccountLink> ·</span>
            )}
            {co.order_date && !editingOrderDate && (
              <span title="Date à laquelle le client a réellement passé sa commande" className="inline-flex items-center gap-1">
                commande du <strong>{fmtDate(co.order_date)}</strong>
                {!isEditing && co.status !== "ANNULEE" && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-[10px] text-[#8C6B2F] hover:bg-[#FBF4E9]"
                    onClick={startEditOrderDate}
                    data-testid="co-edit-order-date-btn"
                    title="Corriger la date à laquelle le client a passé la commande (ex: saisie décalée)"
                  >
                    <CalendarClock className="w-3 h-3 mr-1" /> modifier
                  </Button>
                )}
                <span>·</span>
              </span>
            )}
            {editingOrderDate && (
              <span className="inline-flex items-center gap-1" data-testid="co-order-date-editor">
                commande du
                <Input
                  type="datetime-local"
                  value={orderDateDraft}
                  onChange={(e) => setOrderDateDraft(e.target.value)}
                  className="h-7 w-44 text-xs"
                  data-testid="co-order-date-input"
                />
                <Button
                  size="sm"
                  className="h-7 bg-[#1F4E3D] hover:bg-[#173B2E] text-white"
                  onClick={saveOrderDate}
                  disabled={savingOrderDate}
                  data-testid="co-order-date-save"
                >
                  {savingOrderDate ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 text-[#8C6B2F]"
                  onClick={() => setEditingOrderDate(false)}
                  disabled={savingOrderDate}
                >
                  <X className="w-3 h-3" />
                </Button>
                <span>·</span>
              </span>
            )}
            {!editingDate ? (
              <>
                <span>livraison souhaitée <strong>{fmtDate(co.delivery_date_target)}</strong></span>
                {/* Phase 87o-bis (15/06/2026) — Bouton "modifier" autorisé tant
                    que la commande n'est pas LIVREE ou ANNULEE (PAYEE était trop
                    restrictif : entre paiement et livraison, le client peut
                    légitimement vouloir adapter la date d'arrivée). */}
                {!isEditing && !["LIVREE", "ANNULEE"].includes(co.status) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-[10px] text-[#8C6B2F] hover:bg-[#FBF4E9]"
                    onClick={startEditDate}
                    data-testid="co-edit-date-btn"
                    title="Modifier la date de livraison (propagée au BL lié si non signé)"
                  >
                    <CalendarClock className="w-3 h-3 mr-1" /> modifier
                  </Button>
                )}
              </>
            ) : (
              <span className="inline-flex items-center gap-1">
                <Input
                  type="datetime-local"
                  value={dateDraft}
                  onChange={(e) => setDateDraft(e.target.value)}
                  className="h-7 text-xs w-[180px]"
                  data-testid="co-edit-date-input"
                />
                <Button size="sm" className="h-7 bg-[#1F4E3D] hover:bg-[#173B2E]" onClick={saveDate} disabled={savingDate} data-testid="co-edit-date-save">
                  {savingDate ? "…" : "OK"}
                </Button>
                <Button size="sm" variant="ghost" className="h-7" onClick={() => setEditingDate(false)} data-testid="co-edit-date-cancel">
                  <X className="w-3.5 h-3.5" />
                </Button>
              </span>
            )}
          </div>
        </div>
        {isEditable && !isEditing && (
          <Button variant="outline" size="sm" onClick={startEdit} data-testid="co-edit-btn">
            <Pencil className="w-4 h-4 mr-1.5" /> Modifier la commande
          </Button>
        )}
        {!isEditing && (
          <Button
            variant="outline"
            size="sm"
            onClick={printPdf}
            disabled={printing}
            data-testid="co-print-btn"
            title="Génère le PDF du bon de commande à imprimer ou envoyer au client"
          >
            <Printer className="w-4 h-4 mr-1.5" /> {printing ? "Génération…" : "Imprimer / PDF"}
          </Button>
        )}
        {!isEditing && co.distributor_id === "shop-online-virtual" && (
          <Button
            variant="outline"
            size="sm"
            onClick={printShippingLabel}
            disabled={printingLabel}
            data-testid="co-print-label-btn"
            title="Étiquette d'expédition A6 prête à coller sur le colis (nom, adresse, QR code, mention frais)"
            className="border-[#8C6B2F] text-[#8C6B2F] hover:bg-[#FBF4E9]"
          >
            <PackageCheck className="w-4 h-4 mr-1.5" /> {printingLabel ? "Génération…" : "Étiquette colis"}
          </Button>
        )}
        {!isEditing && (
          <Button
            variant="outline"
            size="sm"
            onClick={duplicate}
            disabled={duplicating}
            data-testid="co-duplicate-btn"
            title="Crée une nouvelle commande à partir de celle-ci (client + lignes + prix copiés)"
          >
            <Copy className="w-4 h-4 mr-1.5" /> {duplicating ? "Duplication…" : "Dupliquer"}
          </Button>
        )}
        {/* Phase 101du.7 — Actions avancées regroupées dans un menu pour désencombrer */}
        {!isEditing && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="border-[#8C6B2F] text-[#8C6B2F] hover:bg-[#FBF4E9]"
                data-testid="co-advanced-menu-trigger"
                title="Outils de correction (liens orphelins, verrous, etc.)"
              >
                <MoreHorizontal className="w-4 h-4 mr-1.5" /> Actions avancées
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-[#8C6B2F]">
                Correction / dépannage
              </DropdownMenuLabel>
              <DropdownMenuItem
                onClick={repairLinks}
                disabled={repairing}
                data-testid="co-repair-btn"
                className="cursor-pointer"
              >
                <AlertTriangle className="w-4 h-4 mr-2 text-[#8C6B2F]" />
                <div className="flex-1">
                  <div className="text-sm">{repairing ? "Réparation…" : "Réparer lien OF"}</div>
                  <div className="text-[10px] text-[#575A57]">Diagnostique et répare les liens OF/BL orphelins</div>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={unlockCo}
                disabled={unlocking}
                data-testid="co-unlock-btn"
                className="cursor-pointer"
              >
                <AlertTriangle className="w-4 h-4 mr-2 text-[#C05C47]" />
                <div className="flex-1">
                  <div className="text-sm">{unlocking ? "…" : "Débloquer la commande"}</div>
                  <div className="text-[10px] text-[#575A57]">Nettoie les verrous et références orphelines</div>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {isEditing && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={cancelEdit} disabled={saving} data-testid="co-edit-cancel">
              <X className="w-4 h-4 mr-1.5" /> Annuler
            </Button>
            <Button size="sm" onClick={saveEdit} disabled={saving} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="co-edit-save">
              <Save className="w-4 h-4 mr-1.5" /> {saving ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </div>
        )}
        {!isEditing && co.status !== "ANNULEE" && co.status !== "PAYEE" && (
          <CancelCascadeDialog co={co} onCancelled={load} />
        )}
      </div>

      {/* Bandeau historique annulation */}
      {co.status === "ANNULEE" && co.cancelled_at && (
        <div className="bg-[#FCEEEA] border border-[#E8C1B4] rounded-lg px-4 py-3 text-sm text-[#7A3324] flex items-start gap-2" data-testid="co-cancelled-banner">
          <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-[#C05C47]" />
          <div className="flex-1">
            <div className="font-medium">
              Commande annulée le {fmtDateTime(co.cancelled_at)}
              {co.cancelled_by ? <span className="font-normal"> par <strong>{co.cancelled_by}</strong></span> : null}
            </div>
            {co.cancellation_summary && (
              <div className="text-xs text-[#5C2F22] mt-0.5">{co.cancellation_summary}</div>
            )}
          </div>
        </div>
      )}

      {/* Bandeau projet : explique le mode brouillon */}
      {isEditable && !isEditing && (
        <div className="bg-[#FBF4E9] border border-[#E8D3A8] rounded-lg px-4 py-3 text-sm text-[#8C6B2F] flex items-center gap-2" data-testid="draft-banner">
          <Pencil className="w-4 h-4 flex-shrink-0" />
          <span>
            Cette commande est en <strong>projet</strong>. Vous pouvez encore modifier les lignes, le client ou la date de livraison. Cliquez sur <strong>Valider la commande</strong> pour la verrouiller, ou sur <strong>Modifier</strong> pour ajuster.
          </span>
        </div>
      )}

      {/* Workflow visual */}
      {!isEditing && (
        <Card className="border-[#E5E5E0]">
          <CardContent className="p-5">
            <div className="flex items-center justify-between flex-wrap gap-2">
              {steps.map((s, i) => {
                const Icon = s.icon;
                return (
                  <div key={s.key} className="flex items-center gap-2 flex-1 min-w-[120px]">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center ${s.done ? "bg-[#1F4E3D] text-white" : "bg-[#F0F0EB] text-[#A3A6A3]"}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <span className={`text-xs ${s.done ? "text-[#1A1C1A] font-medium" : "text-[#A3A6A3]"}`}>{s.label}</span>
                    {i < steps.length - 1 && <div className={`flex-1 h-px ${s.done ? "bg-[#1F4E3D]" : "bg-[#E5E5E0]"}`} />}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Workflow actions */}
      {!isEditing && (
        <Card className="border-[#E5E5E0]">
          <CardContent className="p-4 flex flex-wrap items-center gap-2">
            <span className="text-xs text-[#575A57] uppercase tracking-wide mr-2">Actions</span>
            {co.status === "NOUVELLE" && (
              <Button size="sm" variant="outline" onClick={() => updateStatus("VALIDEE")} data-testid="co-validate">Valider la commande</Button>
            )}
            {!co.bl_id && co.status !== "ANNULEE" && (
              <Button
                size="sm"
                variant="outline"
                onClick={generateOf}
                className="border-[#8C6B2F] text-[#8C6B2F] hover:bg-[#FBF4E9]"
                data-testid="co-generate-of"
                title="Crée un Ordre de Fabrication pré-rempli avec les lignes de cette commande"
              >
                <Factory className="w-3.5 h-3.5 mr-1.5" /> Mettre en fabrication
              </Button>
            )}
            {!co.bl_id && co.status !== "ANNULEE" && (
              <Button
                size="sm"
                onClick={() => generateBl(false)}
                disabled={!availability?.is_fully_servable && !co.reseller_id}
                className="bg-[#1F4E3D] hover:bg-[#173B2E] disabled:opacity-50"
                data-testid="co-generate-bl"
                title={
                  availability?.is_fully_servable
                    ? ""
                    : co.reseller_id
                      ? "Lots dédiés insuffisants — vous pourrez forcer le stock existant"
                      : "Stock insuffisant — produisez d'abord ou attendez le réapprovisionnement"
                }
              >
                <FileText className="w-3.5 h-3.5 mr-1.5" /> Générer BL
              </Button>
            )}
            {/* Phase 87ak — Bouton sortie de stock 1-clic */}
            {co.bl_id && bl && !bl.transfer_executed && co.status !== "ANNULEE" && (
              <Button
                size="sm"
                onClick={executeBlTransfer}
                disabled={shippingInProgress}
                className="bg-[#8C6B2F] hover:bg-[#735524] text-white"
                data-testid="co-execute-transfer"
                title="Exécute la sortie physique des lots du dépôt source vers le destinataire"
              >
                <TruckIcon className="w-3.5 h-3.5 mr-1.5" />
                {shippingInProgress ? "Sortie en cours…" : "Exécuter la sortie de stock"}
              </Button>
            )}
            {!co.invoice_id && co.status !== "ANNULEE" && (
              <Button size="sm" variant="outline" onClick={generateInvoice} data-testid="co-generate-invoice">
                <FileSignature className="w-3.5 h-3.5 mr-1.5" /> Générer facture
              </Button>
            )}
            {/* Phase 87o (15/06/2026) — Bouton "Vérifier liaison revendeur" masqué pour
                les commandes B2C web (distributor_id = "shop-online-virtual"). Un client
                web n'a pas de "revendeur" — c'est un client final direct. */}
            {co.status !== "ANNULEE" && co.distributor_id !== "shop-online-virtual" && (
              <Button
                size="sm"
                variant="outline"
                onClick={relinkReseller}
                className="border-[#8C6B2F] text-[#8C6B2F] hover:bg-[#FBF4E9]"
                title="Recalcule reseller_id et cascade aux OF/lots — utile pour rattraper une cmd créée avant la liaison revendeur"
                data-testid="co-relink-reseller"
              >
                <Link2 className="w-3.5 h-3.5 mr-1.5" /> Vérifier liaison revendeur
              </Button>
            )}
            {co.status !== "ANNULEE" && co.status !== "FACTUREE" && co.status !== "PAYEE" && co.reseller_id && (
              <Button
                size="sm"
                variant="outline"
                onClick={applyResellerPrices}
                className="border-[#5A8F76] text-[#1F4E3D] hover:bg-[#EAF4EE]"
                title="Recalcule les unit_price selon les prix négociés du revendeur (overrides + rabais global)"
                data-testid="co-apply-reseller-prices"
              >
                <FileSignature className="w-3.5 h-3.5 mr-1.5" /> Appliquer prix revendeur
              </Button>
            )}
            {co.status === "PRETE" && (
              <Button size="sm" variant="outline" onClick={() => updateStatus("LIVREE")} data-testid="co-mark-delivered">Marquer livrée</Button>
            )}
            {co.payment_method === "twint" && co.status === "PAIEMENT_EN_ATTENTE" && (
              <Button
                size="sm"
                onClick={confirmTwintPayment}
                disabled={confirmingTwint}
                className="bg-[#EE1E5A] hover:bg-[#D01A50] text-white"
                title="Marque la commande comme payée par TWINT — à faire après avoir vérifié la réception dans votre app TWINT"
                data-testid="co-confirm-twint"
              >
                {confirmingTwint ? "Traitement…" : "Marquer TWINT reçu"}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* En-tête commande (lecture ou édition) */}
      {isEditing && (
        <Card className="border-[#E5E5E0]" data-testid="co-edit-header">
          <CardContent className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="mb-1.5 block">Client *</Label>
              <Select value={draft.distributor_id} onValueChange={(v) => setDraft({ ...draft, distributor_id: v })}>
                <SelectTrigger data-testid="co-edit-distributor"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
                <SelectContent>
                  {clients.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.name}{d.code ? ` (${d.code})` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block">Date de livraison souhaitée</Label>
              <Input
                type="datetime-local"
                value={draft.delivery_date_target}
                onChange={(e) => setDraft({ ...draft, delivery_date_target: e.target.value })}
                data-testid="co-edit-date"
              />
            </div>
            <div>
              <Label className="mb-1.5 block">Notes</Label>
              <Input
                value={draft.notes}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                placeholder="Instructions spéciales…"
                data-testid="co-edit-notes"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Disponibilité stock */}
      {!isEditing && availability && !co.bl_id && (
        <Card className={`border-[#E5E5E0] ${availability.is_fully_servable ? "" : "ring-1 ring-[#E8C1B4]"}`} data-testid="stock-availability-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <Package className="w-4 h-4 text-[#1F4E3D]" />
              <span className="text-sm font-medium text-[#1A1C1A]">
                {availability.use_dedicated_only
                  ? "Disponibilité — lots dédiés à cette commande revendeur"
                  : "Disponibilité stock (priorité distributeurs automatiques)"}
              </span>
              {availability.use_dedicated_only && (
                <Badge
                  className="bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D9B8] text-[10px] uppercase tracking-wider"
                  data-testid="reseller-dedicated-badge"
                >
                  🔒 Fabrication dédiée
                </Badge>
              )}
              {availability.is_fully_servable ? (
                <Badge className="bg-[#EAF4EE] text-[#1F4E3D] border-[#C5DBCF] ml-auto">Stock suffisant</Badge>
              ) : (
                <Badge className="bg-[#FCEEEA] text-[#C05C47] border-[#E8C1B4] ml-auto">
                  <AlertTriangle className="w-3 h-3 mr-1" /> Manque {availability.total_missing} unité{availability.total_missing > 1 ? "s" : ""}
                </Badge>
              )}
            </div>
            {availability.use_dedicated_only && (
              <div className="text-[11px] text-[#8C6B2F] bg-[#FBF4E9] border border-[#E8D9B8] rounded p-2 mb-3" data-testid="dedicated-info">
                Cette commande est en mode <b>fabrication dédiée</b> : seuls les lots produits expressément pour cette commande sont éligibles, garantissant une DLC maximale en boutique.
                {!availability.is_fully_servable && (
                  <> Produisez un OF (il sera automatiquement dédié), ou utilisez « Forcer le stock existant » au moment de la génération du BL pour piocher dans le FIFO classique.</>
                )}
              </div>
            )}
            <div className="space-y-2">
              {availability.lines.map((ln, idx) => (
                <div key={ln.line_id || idx} className={`border border-[#E5E5E0] rounded p-3 ${ln.unavailable ? "bg-[#F4F4F0] opacity-70" : "bg-[#FAFAF7]"}`} data-testid={`avail-line-${idx}`}>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="text-sm font-medium text-[#1A1C1A] flex items-center gap-2">
                      {ln.product_name}
                      {ln.unavailable && (
                        <span className="text-[10px] uppercase tracking-wider bg-[#FCEEEA] border border-[#E8C1B4] text-[#C05C47] px-1.5 py-0.5 rounded">
                          Indisponible
                        </span>
                      )}
                    </div>
                    {!ln.unavailable && (
                      <div className="text-xs">
                        <span className={ln.is_complete ? "text-[#1F4E3D]" : "text-[#C05C47]"}>
                          {ln.quantity_available} / {ln.quantity_needed} servies
                        </span>
                        {!ln.is_complete && <span className="text-[#C05C47] ml-2">· manque {ln.quantity_missing}</span>}
                      </div>
                    )}
                  </div>
                  {!ln.unavailable && ln.picked_lots.length > 0 && (
                    <div className="text-[11px] text-[#575A57] space-y-0.5">
                      {ln.picked_lots.map((p, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span className="font-mono">{p.lot_number}</span>
                          <span>·</span>
                          <span>{p.quantity} pcs</span>
                          <span>·</span>
                          <span className={p.distributor_type === "AUTOMATIQUE" ? "text-[#1F4E3D]" : "text-[#5847A6]"}>
                            {p.distributor_name}
                          </span>
                          {p.dlc && <><span>·</span><span>DLC {fmtDate(p.dlc)}</span></>}
                          {p.preselected && (
                            <Badge className="bg-[#EAF4EE] text-[#1F4E3D] border-[#C5DBCF] text-[9px] uppercase tracking-wider" data-testid={`avail-line-preselected-${idx}-${i}`}>
                              🎯 Pré-sélectionné
                            </Badge>
                          )}
                          {p.beyond_spread && (
                            <Badge className="bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D3A8] text-[9px] uppercase tracking-wider" title="Ce lot a une date de production éloignée du lot principal — DLC potentiellement différente" data-testid={`avail-line-beyond-spread-${idx}-${i}`}>
                              ⚠ DLC mixte
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {!ln.unavailable && !ln.is_complete && (
                    <div className="text-[11px] text-[#8C6B2F] mt-1.5 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> Produisez en avance ou attendez un réapprovisionnement
                    </div>
                  )}
                </div>
              ))}
            </div>
            {!availability.is_fully_servable && (
              <div className="mt-3 text-xs">
                <Link to="/ordres" className="text-[#1F4E3D] hover:underline inline-flex items-center gap-1">
                  <Factory className="w-3.5 h-3.5" /> Aller créer un Ordre de Fabrication
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Liaisons */}
      {!isEditing && ((co.of_ids || []).length > 0 || co.bl_id || co.invoice_id) && (
        <Card className="border-[#E5E5E0]">
          <CardContent className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            {(co.of_ids || []).length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wide text-[#A3A6A3]">Ordres de fabrication</div>
                <Link
                  to={`/ordres?co=${co.id}`}
                  className="text-sm text-[#8C6B2F] hover:underline flex items-center gap-1 mt-0.5"
                  data-testid="co-linked-ofs"
                >
                  <Factory className="w-3.5 h-3.5" /> {(co.of_ids || []).length} OF lié{(co.of_ids || []).length > 1 ? "s" : ""} · Ouvrir la liste <ExternalLink className="w-3 h-3" />
                </Link>
              </div>
            )}
            {co.bl_id && (
              <div>
                <div className="text-xs uppercase tracking-wide text-[#A3A6A3]">Bon de livraison</div>
                <Link to={`/bons-livraison/${co.bl_id}`} className="text-sm text-[#1F4E3D] hover:underline flex items-center gap-1">
                  <FileText className="w-3.5 h-3.5" /> {bl?.reference || "Ouvrir le BL"} <ExternalLink className="w-3 h-3" />
                </Link>
                {/* Phase 87ak — Indicateur de sortie de stock */}
                {bl && (
                  <div className="mt-1 inline-flex items-center gap-1 text-[10px]" data-testid="bl-transfer-state">
                    {bl.transfer_executed ? (
                      <span className="px-1.5 py-0.5 rounded-full bg-[#E8F1EA] text-[#1F4E3D] border border-[#B9D6BD] inline-flex items-center gap-1">
                        <CheckCircle2 className="w-2.5 h-2.5" /> Sortie effectuée
                      </span>
                    ) : (
                      <span className="px-1.5 py-0.5 rounded-full bg-[#FCEEEA] text-[#8B1A1A] border border-[#E8C1B4] inline-flex items-center gap-1">
                        <AlertTriangle className="w-2.5 h-2.5" /> Sortie de stock non exécutée
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
            {co.invoice_id && (
              <div>
                <div className="text-xs uppercase tracking-wide text-[#A3A6A3]">Facture</div>
                <Link to={`/factures/${co.invoice_id}`} className="text-sm text-[#1F4E3D] hover:underline flex items-center gap-1">
                  <FileSignature className="w-3.5 h-3.5" /> Ouvrir la facture <ExternalLink className="w-3 h-3" />
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Lignes — tableau (lecture ou édition) */}
      <Card className="border-[#E5E5E0]">
        <CardContent className="p-0">
          {isEditing ? (
            <div className="p-4 space-y-2" data-testid="co-edit-lines">
              <div className="flex items-center justify-between mb-1">
                <Label>Lignes de commande</Label>
                <div className="flex items-center gap-2">
                  <PackInsertDialog
                    onInsert={insertPackLines}
                    defaultVatRate={2.6}
                    testIdPrefix="co-edit-pack-insert"
                  />
                  <Button variant="outline" size="sm" onClick={addDraftLine} data-testid="co-edit-add-line">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter une ligne
                  </Button>
                </div>
              </div>
              {draft.lines.length === 0 ? (
                <div className="text-xs text-[#A3A6A3] border border-dashed border-[#E5E5E0] rounded p-4 text-center">
                  Cliquez sur « Ajouter une ligne » pour insérer un produit.
                </div>
              ) : (
                draft.lines.map((ln, idx) => (
                  <div
                    key={idx}
                    className={`grid grid-cols-12 gap-2 items-end p-3 border rounded ${ln.unavailable ? "bg-[#FCEEEA]/40 border-[#E8C1B4]" : "bg-[#FAFAF7] border-[#E5E5E0]"}`}
                    data-testid={`co-edit-line-${idx}`}
                  >
                    {ln.pack_label && (
                      <div className="col-span-12 -mb-1">
                        <Badge className="bg-[#EAF4EE] text-[#1F4E3D] border-[#C5DBCF] text-[10px]" data-testid={`co-edit-line-pack-${idx}`}>
                          Pack · {ln.pack_label}
                        </Badge>
                      </div>
                    )}
                    <div className="col-span-4">
                      <Label className="text-[11px] mb-1 block">Produit *</Label>
                      <Select value={ln.product_id} onValueChange={(v) => onProductChange(idx, v)}>
                        <SelectTrigger className="h-8 text-xs" data-testid={`co-edit-line-product-${idx}`}>
                          <SelectValue placeholder="Choisir…" />
                        </SelectTrigger>
                        <SelectContent>
                          {sortByName(products).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-1">
                      <Label className="text-[11px] mb-1 block">Qté *</Label>
                      <Input
                        type="number"
                        step="1"
                        min="1"
                        value={ln.quantity}
                        onChange={(e) => updateDraftLine(idx, { quantity: e.target.value })}
                        className="h-8 text-xs"
                        data-testid={`co-edit-line-qty-${idx}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-[11px] mb-1 block">PU CHF</Label>
                      <Input
                        type="number"
                        step="0.10"
                        value={ln.unit_price}
                        onChange={(e) => updateDraftLine(idx, { unit_price: e.target.value })}
                        className="h-8 text-xs"
                        placeholder="—"
                        data-testid={`co-edit-line-price-${idx}`}
                      />
                    </div>
                    <div className="col-span-1">
                      <Label className="text-[11px] mb-1 block">TVA %</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={ln.vat_rate}
                        onChange={(e) => updateDraftLine(idx, { vat_rate: e.target.value })}
                        className="h-8 text-xs"
                        data-testid={`co-edit-line-vat-${idx}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-[11px] mb-1 block">Note ligne</Label>
                      <Input
                        value={ln.notes}
                        onChange={(e) => updateDraftLine(idx, { notes: e.target.value })}
                        className="h-8 text-xs"
                        placeholder="—"
                        data-testid={`co-edit-line-notes-${idx}`}
                      />
                    </div>
                    <div className="col-span-2 flex items-center gap-1 justify-end">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => updateDraftLine(idx, { unavailable: !ln.unavailable })}
                        className={`h-8 px-2 text-xs ${ln.unavailable ? "text-[#C05C47]" : "text-[#575A57]"}`}
                        title={ln.unavailable ? "Marquer disponible" : "Marquer indisponible"}
                        data-testid={`co-edit-line-unavail-${idx}`}
                      >
                        {ln.unavailable ? <><Eye className="w-3.5 h-3.5 mr-1" /> Dispo.</> : <><EyeOff className="w-3.5 h-3.5 mr-1" /> Indispo.</>}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeDraftLine(idx)}
                        className="h-8 w-8 text-[#C05C47]"
                        data-testid={`co-edit-line-remove-${idx}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                    {/* Phase 101bu — Sélecteur de lot OBLIGATOIRE (mode revente).
                        Plus d'auto LIFO/FIFO : l'utilisateur choisit manuellement le lot. */}
                    {co?.sale_type === "resale" && ln.product_id && (
                      <div className="col-span-12">
                        <Label className="text-[11px] mb-1 block text-[#8C6B2F]">
                          Lot / DLC · <span className="text-[#C05C47]">obligatoire — choix libre</span>
                        </Label>
                        <Select
                          value={ln.preselected_lot_id || ""}
                          onValueChange={(v) => updateDraftLine(idx, { preselected_lot_id: v })}
                        >
                          <SelectTrigger
                            className={`h-8 text-xs bg-white ${!ln.preselected_lot_id ? "border-[#C05C47]" : ""}`}
                            data-testid={`co-edit-line-lot-${idx}`}
                          >
                            <SelectValue placeholder="— Choisir un lot —" />
                          </SelectTrigger>
                          <SelectContent>
                            {(lotsByProduct[ln.product_id] || []).map((l) => {
                              const dlcTxt = l.dlc ? fmtDate(l.dlc) : "sans DLC";
                              const daysTxt = l.days_to_dlc != null
                                ? (l.days_to_dlc < 0 ? " ⛔ périmé"
                                  : l.days_to_dlc <= 3 ? ` ⚠ J-${l.days_to_dlc}`
                                    : ` (J-${l.days_to_dlc})`)
                                : "";
                              const insufficient = Number(l.quantity || 0) < Number(ln.quantity || 0);
                              return (
                                <SelectItem
                                  key={l.id}
                                  value={l.id}
                                  disabled={insufficient}
                                  data-testid={`co-edit-line-lot-option-${idx}-${l.lot_number}`}
                                >
                                  <span className="font-mono">{l.lot_number || "(sans n°)"}</span>
                                  <span className="ml-2">· DLC {dlcTxt}{daysTxt}</span>
                                  <span className="ml-2 text-[#849F90]">· {l.quantity} dispo{insufficient ? " (insuffisant)" : ""}</span>
                                </SelectItem>
                              );
                            })}
                            {(lotsByProduct[ln.product_id] || []).length === 0 && (
                              <SelectItem value="__NONE__" disabled>
                                <span className="italic text-[#A3A6A3]">Aucun lot dispo à Conthey</span>
                              </SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Produit</TableHead>
                  <TableHead>SKU</TableHead>
                  <TableHead className="text-right">Qté</TableHead>
                  <TableHead className="text-right">PU CHF</TableHead>
                  <TableHead className="text-right">TVA %</TableHead>
                  <TableHead className="text-right">Total HT</TableHead>
                  <TableHead className="text-right" data-testid="margin-header">Marge</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(co.lines || []).map((ln, idx) => {
                  const prod = products.find((p) => p.id === ln.product_id);
                  const cost = prod?.cost_price ? Number(prod.cost_price) : null;
                  const price = Number(ln.unit_price) || 0;
                  const marginPct = cost && cost > 0 && price > cost ? Math.round(((price - cost) / price) * 100) : null;
                  const marginColor =
                    marginPct == null ? "text-[#A3A6A3]" :
                      marginPct >= 40 ? "bg-[#E8EEE9] border-[#1F4E3D]/30 text-[#1F4E3D]" :
                        marginPct >= 20 ? "bg-[#FBF4E9] border-[#E8D3A8] text-[#8C6B2F]" :
                          "bg-[#FCEEEA] border-[#C05C47]/30 text-[#C05C47]";
                  const isUnavail = !!ln.unavailable;
                  return (
                    <TableRow key={ln.id || idx} className={isUnavail ? "opacity-50" : ""} data-testid={`co-line-row-${idx}`}>
                      <TableCell className={isUnavail ? "line-through" : ""}>
                        <div className="flex items-center gap-2 flex-wrap">
                          {ln.product_name}
                          {ln.pack_label && (
                            <span className="text-[10px] uppercase tracking-wider bg-[#EAF4EE] border border-[#C5DBCF] text-[#1F4E3D] px-1.5 py-0.5 rounded font-medium" title={`Issue du pack : ${ln.pack_label}`} data-testid={`co-line-pack-${idx}`}>
                              Pack · {ln.pack_label}
                            </span>
                          )}
                          {isUnavail && (
                            <span className="text-[10px] uppercase tracking-wider bg-[#FCEEEA] border border-[#E8C1B4] text-[#C05C47] px-1.5 py-0.5 rounded no-underline" data-testid={`co-line-unavail-${idx}`}>
                              Indisponible
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className={`font-mono text-xs ${isUnavail ? "line-through" : ""}`}>{ln.product_sku || "—"}</TableCell>
                      <TableCell className={`text-right font-mono ${isUnavail ? "line-through" : ""}`}>{fmtNum(ln.quantity, ln.unit)}</TableCell>
                      <TableCell className={`text-right font-mono ${isUnavail ? "line-through" : ""}`}>{ln.unit_price ? Number(ln.unit_price).toFixed(2) : "—"}</TableCell>
                      <TableCell className={`text-right font-mono text-xs ${isUnavail ? "line-through" : ""}`}>{ln.vat_rate}%</TableCell>
                      <TableCell className={`text-right font-mono ${isUnavail ? "line-through" : ""}`}>
                        {((ln.quantity || 0) * (ln.unit_price || 0)).toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right">
                        {!isUnavail && marginPct != null ? (
                          <span
                            className={`inline-flex items-center gap-1 text-[11px] font-mono font-semibold border rounded-full px-2 py-0.5 ${marginColor}`}
                            title={`Coût ${cost.toFixed(2)} CHF / PU ${price.toFixed(2)} CHF`}
                            data-testid={`margin-${idx}`}
                          >
                            {marginPct}%
                          </span>
                        ) : (
                          <span className="text-[#A3A6A3] text-xs italic">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
          <div className="px-5 py-3 border-t border-[#E5E5E0] flex justify-end items-center gap-6 text-sm flex-wrap">
            {!isEditing && globalMargin != null && (
              <span
                className={`text-[11px] font-mono font-semibold border rounded-full px-3 py-1 ${
                  globalMargin >= 40 ? "bg-[#E8EEE9] border-[#1F4E3D]/30 text-[#1F4E3D]" :
                    globalMargin >= 20 ? "bg-[#FBF4E9] border-[#E8D3A8] text-[#8C6B2F]" :
                      "bg-[#FCEEEA] border-[#C05C47]/30 text-[#C05C47]"
                }`}
                title={`Coût total ${totalCost.toFixed(2)} CHF · Marge brute ${(totalHT - totalCost).toFixed(2)} CHF`}
                data-testid="global-margin"
              >
                Marge globale {globalMargin}%
              </span>
            )}
            <span className="text-[#575A57]">Total HT (lignes disponibles)</span>
            <span className="font-mono font-medium text-[#1A1C1A]" data-testid="co-total-ht">{totalHT.toFixed(2)} CHF</span>
          </div>
        </CardContent>
      </Card>

      {!isEditing && co.notes && (
        <Card className="border-[#E5E5E0]">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-[#A3A6A3] mb-1">Notes</div>
            <div className="text-sm">{co.notes}</div>
          </CardContent>
        </Card>
      )}

      {/* Phase 101du.5+ — Économie négociée revendeur */}
      {!isEditing && savings && savings.total_saving > 0 && (
        <Card className="border-[#5A8F76]/40 bg-gradient-to-br from-[#F4F9F5] to-white" data-testid="co-reseller-savings">
          <CardContent className="p-5">
            <div className="flex items-center justify-between gap-4 flex-wrap mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#EAF4EE] border border-[#5A8F76]/30 flex items-center justify-center">
                  <TrendingDown className="w-4 h-4 text-[#1F4E3D]" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#5A8F76] font-semibold">Économie négociée</div>
                  <h3 className="font-heading text-base text-[#1A1C1A]">Prix revendeur vs catalogue</h3>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-[#575A57]">Économie totale</div>
                <div className="font-mono text-2xl font-semibold text-[#1F4E3D]" data-testid="co-savings-total">
                  −{savings.total_saving.toFixed(2)} CHF
                </div>
                <div className="text-[11px] text-[#5A8F76] font-medium">
                  soit −{savings.pct_saving.toFixed(1)}% en moyenne
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm border-t border-[#5A8F76]/20 pt-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#A3A6A3]">Total catalogue HT</div>
                <div className="font-mono text-[#575A57] line-through" data-testid="co-savings-catalog">
                  {savings.catalog.toFixed(2)} CHF
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#A3A6A3]">Total négocié HT</div>
                <div className="font-mono font-semibold text-[#1F4E3D]" data-testid="co-savings-negotiated">
                  {savings.negotiated.toFixed(2)} CHF
                </div>
              </div>
            </div>

            {savings.details.length > 1 && (
              <details className="mt-3 text-xs">
                <summary className="cursor-pointer text-[#5A8F76] hover:text-[#1F4E3D] font-medium">
                  Détail par ligne ({savings.lines_count})
                </summary>
                <table className="w-full mt-2 border-collapse">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-[#A3A6A3] border-b border-[#F0F0EB]">
                      <th className="text-left py-1 font-medium">Produit</th>
                      <th className="text-right py-1 font-medium">Qté</th>
                      <th className="text-right py-1 font-medium">Catalogue</th>
                      <th className="text-right py-1 font-medium">Négocié</th>
                      <th className="text-right py-1 font-medium">Économie</th>
                    </tr>
                  </thead>
                  <tbody>
                    {savings.details.map((d, i) => (
                      <tr key={i} className="border-b border-[#F5F5F0] last:border-0">
                        <td className="py-1 text-[#1A1C1A]">{d.product_name}</td>
                        <td className="py-1 text-right font-mono text-[#575A57]">{d.quantity}</td>
                        <td className="py-1 text-right font-mono text-[#A3A6A3] line-through">{d.catalog_unit.toFixed(2)}</td>
                        <td className="py-1 text-right font-mono text-[#1A1C1A]">{d.negotiated_unit.toFixed(2)}</td>
                        <td className="py-1 text-right font-mono font-semibold text-[#1F4E3D]">
                          {d.line_saving > 0 ? `−${d.line_saving.toFixed(2)}` : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            )}
          </CardContent>
        </Card>
      )}

      {!isEditing && (co.validated_at || co.delivered_at || co.invoiced_at || co.paid_at) && (
        <Card className="border-[#E5E5E0]">
          <CardContent className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            {co.validated_at && <div><div className="text-[#A3A6A3] uppercase tracking-wide">Validée</div><div>{fmtDateTime(co.validated_at)}</div></div>}
            {co.delivered_at && <div><div className="text-[#A3A6A3] uppercase tracking-wide">Livrée</div><div>{fmtDateTime(co.delivered_at)}</div></div>}
            {co.invoiced_at && <div><div className="text-[#A3A6A3] uppercase tracking-wide">Facturée</div><div>{fmtDateTime(co.invoiced_at)}</div></div>}
            {co.paid_at && <div><div className="text-[#A3A6A3] uppercase tracking-wide">Payée</div><div>{fmtDateTime(co.paid_at)}</div></div>}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
